/** A handoff's words (slice 6c §7.1): its card's line, and an ended one's (board 93). */
import { shortTime } from "../chats/format";
import type { Me } from "../../app/me";
import type { Handoff } from "./api";
import { COMPUTER_ONLY } from "./computer-view";

export const firstName = (name: string) => name.trim().split(/\s+/)[0] ?? name;

/** "4 min": how long the taker had it, at least a minute. */
export function heldFor(h: Pick<Handoff, "taken_at" | "ended_at">): string | null {
  if (!h.taken_at || !h.ended_at) return null;
  const ms = new Date(h.ended_at).getTime() - new Date(h.taken_at).getTime();
  if (!Number.isFinite(ms)) return null;
  return `${Math.max(1, Math.round(ms / 60_000))} min`;
}

/** An ended handoff's line (board 93): who, what and when. */
export function endedLine(
  h: Handoff,
  timeZone: string,
  now = new Date(),
): { text: string; meta: string } {
  const who = h.ended_by ? firstName(h.ended_by.name) : "Someone";
  const at = h.ended_at ? shortTime(h.ended_at, timeZone, now) : "";
  if (h.status === "done") {
    const clock = h.ended_how === "timeout" ? " (30 minutes)" : "";
    const took = heldFor(h);
    return {
      text: `${who} finished: ${h.reason}${clock}`,
      meta: took ? `${took} · ${at}` : at,
    };
  }
  if (h.status === "skipped") return { text: `${who} skipped: ${h.reason}`, meta: at };
  return { text: `Nobody took it: ${h.reason}`, meta: at };
}

/** What the person does on the page, one line: under the card's title and the sheet's. */
export const whatToDo = (h: Pick<Handoff, "host">) =>
  h.host
    ? `Finish it on ${h.host}, then tap I'm done.`
    : "Finish it on the computer, then tap I'm done.";

/**
 * Whether the card can show the computer, and whether Take over is here: on
 * a phone, a pi computer's lean box (S-455) is taken over on a computer, and
 * the card says so instead.
 */
export function takeOn(
  computer: Pick<NonNullable<Me["computer"]>, "desktop" | "phone_control"> | null,
  mobile: boolean,
): { showable: boolean; take: boolean; elsewhere: string | null } {
  const showable = computer?.desktop !== false;
  const here = !mobile || computer?.phone_control !== false;
  return { showable, take: showable && here, elsewhere: showable && !here ? COMPUTER_ONLY : null };
}
